import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Scissors, Bone, Bag, Dog, Stethoscope, PawPrint, Storefront, ChatCircleText,
  Star, WarningCircle, MagnifyingGlass,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatMoney, isOutOfStock, isStockLow } from "./utils";
import type { CashierCatalogIcon, CashierCatalogItem, CashierLabels } from "./types";

type IconCmp = React.ComponentType<React.SVGProps<SVGSVGElement> & { weight?: "regular" | "fill" | "bold" | "thin" | "light" | "duotone" }>;

const ICONS: Record<CashierCatalogIcon, IconCmp> = {
  grooming: Scissors as unknown as IconCmp,
  boarding: Bone as unknown as IconCmp,
  product: Bag as unknown as IconCmp,
  training: Dog as unknown as IconCmp,
  vet: Stethoscope as unknown as IconCmp,
  walk: PawPrint as unknown as IconCmp,
  accessory: Storefront as unknown as IconCmp,
  consultation: ChatCircleText as unknown as IconCmp,
};

export function CategoryIcon({ icon, className, weight = "regular" }: { icon: CashierCatalogIcon; className?: string; weight?: "regular" | "fill" }) {
  const I = ICONS[icon] ?? (Bag as unknown as IconCmp);
  return <I className={className} weight={weight} />;
}

export interface CashierProductCardProps {
  item: CashierCatalogItem;
  onAdd: (item: CashierCatalogItem) => void;
  iconForCategory: CashierCatalogIcon;
  labels: CashierLabels;
  locale?: "tr" | "en";
}

export function CashierProductCard({ item, onAdd, iconForCategory, labels, locale = "tr" }: CashierProductCardProps) {
  const out = isOutOfStock(item);
  const low = isStockLow(item);
  return (
    <motion.button
      type="button"
      whileTap={out ? undefined : { scale: 0.96 }}
      transition={{ duration: 0.12 }}
      disabled={out}
      onClick={() => onAdd(item)}
      aria-label={`${item.label} — ${formatMoney(item.priceMinor, labels.currencyPrefix, locale)}`}
      className={cn(
        "group relative flex flex-col items-stretch text-left rounded-biz-card border border-biz-border bg-biz-surface-elevated p-3 min-h-[120px]",
        "transition-all duration-150 ease-biz-out biz-focus-ring",
        out
          ? "opacity-50 grayscale pointer-events-none"
          : "hover:-translate-y-px hover:biz-shadow-md hover:border-biz-primary/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="h-10 w-10 rounded-biz-btn bg-biz-primary-subtle text-biz-primary-subtle-fg flex items-center justify-center shrink-0">
          <CategoryIcon icon={iconForCategory} className="h-5 w-5" />
        </div>
        <div className="flex flex-col items-end gap-1">
          {item.isFavorite && (
            <Star weight="fill" className="h-4 w-4 text-biz-accent" />
          )}
          {item.stockOnHand !== null && (low || out) && (
            <span
              className={cn(
                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold",
                out ? "bg-biz-danger-subtle text-biz-danger" : "bg-biz-warning-subtle text-biz-warning",
              )}
            >
              <WarningCircle weight="fill" className="h-3 w-3" />
              {out ? labels.catalog.outOfStock : labels.catalog.stockLow}
            </span>
          )}
        </div>
      </div>

      <div className="mt-2 flex-1 text-sm font-semibold text-biz-foreground leading-snug line-clamp-2">
        {item.label}
      </div>

      <div className="mt-2 flex items-baseline justify-between gap-2">
        <span className="font-mono-num text-base font-bold text-biz-foreground tabular-nums">
          {formatMoney(item.priceMinor, labels.currencyPrefix, locale)}
        </span>
        {item.sku && (
          <span className="font-mono-num text-[10px] text-biz-foreground-subtle truncate">{item.sku}</span>
        )}
      </div>
    </motion.button>
  );
}

export interface CashierCatalogGridProps {
  items: CashierCatalogItem[];
  categoryIconMap: Record<string, CashierCatalogIcon>;
  onAddItem: (item: CashierCatalogItem) => void;
  emptyLabel: string;
  labels: CashierLabels;
  locale?: "tr" | "en";
}

export function CashierCatalogGrid({
  items, categoryIconMap, onAddItem, emptyLabel, labels, locale = "tr",
}: CashierCatalogGridProps) {
  if (items.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8 text-biz-foreground-muted">
        <MagnifyingGlass className="h-10 w-10 mb-3 text-biz-foreground-subtle" />
        <p className="text-sm">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 p-4 biz-scrollbar overflow-auto">
      <AnimatePresence initial={false}>
        {items.map((item, i) => (
          <motion.div
            key={item.id}
            layout
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0, transition: { delay: Math.min(i * 0.015, 0.25) } }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16 }}
          >
            <CashierProductCard
              item={item}
              onAdd={onAddItem}
              iconForCategory={categoryIconMap[item.categoryKey] ?? "product"}
              labels={labels}
              locale={locale}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
